<script lang="ts">
  import type { Snippet } from "svelte";

  import FiltersContainer from "./FiltersContainer.svelte";
  import SimpleSelect from "./SimpleSelect.svelte";
  import type {
    Column,
    EqCondition,
    FilterCondition,
    RangeCondition,
    StringColumn,
  } from "./types";

  interface Props {
    filterConditions: FilterCondition[];
    setFilterConditions?: (filters: FilterCondition[]) => void;
    extraFilter?: Snippet;
  }

  let { filterConditions, setFilterConditions, extraFilter }: Props = $props();

  const disabled = $derived(setFilterConditions === undefined);

  function updateFilterForColumn(column: Column, update: FilterCondition) {
    let newFilterConditions = [...filterConditions];
    let condition = newFilterConditions.find((f) => f.column === column);
    if (!condition) {
      newFilterConditions.push(update);
    } else {
      newFilterConditions = newFilterConditions.map((f) =>
        f.column === column ? update : f,
      );
    }
    setFilterConditions?.(newFilterConditions);
  }

  function deleteFilterForColumn(column: Column) {
    const newFilters = filterConditions.filter((f) => f.column !== column);
    setFilterConditions?.(newFilters);
  }

  function updateSelectedValueForColumn(
    column: StringColumn,
    value: string | null,
  ) {
    if (disabled) return;

    if (!value || value === "any") {
      deleteFilterForColumn(column);
    } else {
      updateFilterForColumn(column, {
        type: "eq",
        column,
        value,
      });
    }
  }

  let selectedModel = $derived(
    filterConditions.find<EqCondition>(
      (f): f is EqCondition => f.column === "model" && f.type === "eq",
    )?.value ?? "any",
  );
  let selectedFramework = $derived(
    filterConditions.find<EqCondition>(
      (f): f is EqCondition => f.column === "framework" && f.type === "eq",
    )?.value ?? "any",
  );
  let selectedTokens = $derived(
    filterConditions.find<EqCondition>(
      (f): f is EqCondition => f.column === "tokens" && f.type === "eq",
    )?.value ?? "any",
  );
  let restrictedMetric = $derived<Column>(
    filterConditions.find<RangeCondition>(
      (f): f is RangeCondition => f.type === "range",
    )?.column ?? "ttft_p95",
  );
  let restrictionValueMs = $derived.by<string>(() => {
    let ms =
      filterConditions.find<RangeCondition>(
        (f): f is RangeCondition => f.type === "range",
      )?.maxValue ?? 1;
    return `${ms * 1000}`;
  });

  function onChangeRestriction() {
    const newFilterConditions = [...filterConditions];
    const existingRangeIndex = newFilterConditions.findIndex(
      (f) => f.type === "range",
    );

    const newRangeCondition = {
      type: "range" as const,
      column: restrictedMetric,
      maxValue: Number(restrictionValueMs) / 1000,
    };

    if (existingRangeIndex !== -1) {
      newFilterConditions[existingRangeIndex] = newRangeCondition;
    } else {
      newFilterConditions.push(newRangeCondition);
    }

    setFilterConditions?.(newFilterConditions);
  }
</script>

<div
  class="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6 items-stretch select-none max-w-[900px] self-center"
>
  <FiltersContainer
    active={!disabled}
    class={[
      "order-[-3]",
      disabled && "col-span-2 self-center justify-self-center max-w-fit",
    ]}
  >
    <span>{disabled ? "We ran" : "I want to serve"}</span>
    <SimpleSelect
      class="!max-w-[240px]"
      bind:value={selectedModel}
      onchange={() => updateSelectedValueForColumn("model", selectedModel)}
      options={[
        { label: "Qwen 2.5 7B", value: "Qwen 2.5 7B" },
        { label: "Qwen 3 235B-A22B", value: "Qwen 3 235B-A22B" },
        { label: "Qwen 3 235B-A22B fp8", value: "Qwen 3 235B-A22B fp8" },
        {
          label: "DeepSeek-V3 671B-A37B int4",
          value: "DeepSeek-V3 671B-A37B int4",
        },
        { label: "Llama 3.1 8B", value: "Llama 3.1 8B" },
        { label: "Llama 3.1 8B fp8", value: "Llama 3.1 8B fp8" },
        { label: "Llama 3.1 8B int4", value: "Llama 3.1 8B int4" },
        { label: "Llama 3.1 70B", value: "Llama 3.1 70B" },
        { label: "Llama 3.1 70B fp8", value: "Llama 3.1 70B fp8" },
        { label: "Llama 3.3 70B int4", value: "Llama 3.3 70B int4" },
        { label: "Gemma 3 4B bf16", value: "Gemma 3 4B bf16" },
        { label: "Gemma 3 12B bf16", value: "Gemma 3 12B bf16" },
        { label: "Gemma 3 27B bf16", value: "Gemma 3 27B bf16" },
        { label: "Ministral 8B", value: "Ministral 8B" },
        { label: "Mistral Small 3.1 24B", value: "Mistral Small 3.1 24B" },
      ]}
      {disabled}
    />
    <span>with</span>
    <SimpleSelect
      bind:value={selectedFramework}
      onchange={() =>
        updateSelectedValueForColumn("framework", selectedFramework)}
      options={[
        { label: "any engine", value: "any" },
        { label: "SGLang", value: "sglang" },
        { label: "vLLM", value: "vllm" },
        { label: "TensorRT-LLM", value: "tensorrt-llm" },
      ]}
      {disabled}
    />
  </FiltersContainer>
  {#if !disabled || selectedTokens !== "any"}
    <FiltersContainer active class="self-stretch lg:order-[-1]">
      <span>I expect on average</span>
      <SimpleSelect
        bind:value={selectedTokens}
        onchange={() => {
          updateSelectedValueForColumn("tokens", selectedTokens);
        }}
        options={[
          { label: "128 tokens in / 1024 tokens out", value: "128;1024" },
          { label: "256 tokens in / 2048 tokens out", value: "256;2048" },
          { label: "512 tokens in / 512 tokens out", value: "512;512" },
          { label: "512 tokens in / 4096 tokens out", value: "512;4096" },
          { label: "1024 tokens in / 128 tokens out", value: "1024;128" },
          { label: "1024 tokens in / 1024 tokens out", value: "1024;1024" },
          { label: "2048 tokens in / 256 tokens out", value: "2048;256" },
          { label: "2048 tokens in / 2048 tokens out", value: "2048;2048" },
          { label: "4096 tokens in / 512 tokens out", value: "4096;512" },
        ]}
        {disabled}
      />
    </FiltersContainer>
  {/if}
  {#if !disabled}
    <FiltersContainer active class="lg:order-[-2]">
      <span>Clients should receive</span>
      <SimpleSelect
        bind:value={restrictedMetric}
        onchange={onChangeRestriction}
        options={[
          { label: "the first token", value: "ttft_p95" },
          {
            label: "the last token",
            value: "ttlt_p95",
          },
          { label: "each token", value: "itl_p95" },
        ]}
      ></SimpleSelect>
      <span>in under</span>
      <SimpleSelect
        bind:value={restrictionValueMs}
        onchange={onChangeRestriction}
        options={[
          { label: "10 ms", value: "10" },
          { label: "30 ms", value: "30" },
          { label: "100 ms", value: "100" },
          { label: "300 ms", value: "300" },
          { label: "1 second", value: "1000" },
          { label: "3 seconds", value: "3000" },
          { label: "10 seconds", value: "10000" },
          { label: "30 seconds", value: "30000" },
          { label: "1 minute", value: "60000" },
        ]}
      />
      <span>95% of the time</span>
    </FiltersContainer>
  {/if}
  {#if extraFilter}
    {@render extraFilter()}
  {/if}
</div>
